import { useState, useEffect } from 'react';
import {
  AlertCircle, Save, Check, Download, Calculator, FileSpreadsheet,
  Calendar, Info, Eye, Send, Mail, Clock
} from '../../icons/wp-icons';
import apiClient from '../../api/apiClient';

const KONTENRAHMEN_OPTIONS = [
  { value: 'SKR03', label: 'SKR 03 (Standard)' },
  { value: 'SKR04', label: 'SKR 04' }
];

function BuchhaltungSettings({ readOnly = false }) {
  // DATEV-Einstellungen
  const [beraterNr, setBeraterNr] = useState('');
  const [mandantenNr, setMandantenNr] = useState('');
  const [kontenrahmen, setKontenrahmen] = useState('SKR03');
  const [standardErloeskonto, setStandardErloeskonto] = useState('8400');

  // Automatischer Versand an den Steuerberater
  const [steuerberaterEmail, setSteuerberaterEmail] = useState('');
  const [autoVersandAktiv, setAutoVersandAktiv] = useState(false);
  const [autoVersandRhythmus, setAutoVersandRhythmus] = useState('monatlich');
  const [autoVersandInfo, setAutoVersandInfo] = useState({ letzterVersand: '', letzterZeitraum: '', naechsterZeitraum: null });
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState('');

  // Export-Parameter
  const [exportVon, setExportVon] = useState('');
  const [exportBis, setExportBis] = useState('');
  const [vorschau, setVorschau] = useState(null);

  // UI State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [loadingVorschau, setLoadingVorschau] = useState(false);

  useEffect(() => {
    loadEinstellungen();
    initializeDateRange();
  }, []);

  const initializeDateRange = () => {
    // Standardmaessig aktuelles Quartal
    const now = new Date();
    const quartalStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
    const quartalEnde = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 + 3, 0);

    setExportVon(quartalStart.toISOString().split('T')[0]);
    setExportBis(quartalEnde.toISOString().split('T')[0]);
  };

  const loadEinstellungen = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/buchhaltung/einstellungen');
      const data = response.data;

      setBeraterNr(data.beraterNr || '');
      setMandantenNr(data.mandantenNr || '');
      setKontenrahmen(data.kontenrahmen || 'SKR03');
      setStandardErloeskonto(data.standardErloeskonto || '8400');
      setSteuerberaterEmail(data.steuerberaterEmail || '');
      setAutoVersandAktiv(!!data.autoVersandAktiv);
      setAutoVersandRhythmus(data.autoVersandRhythmus || 'monatlich');
      setAutoVersandInfo({
        letzterVersand: data.autoVersandLetzterVersand || '',
        letzterZeitraum: data.autoVersandLetzterZeitraum || '',
        naechsterZeitraum: data.naechsterZeitraum || null
      });
    } catch (err) {
      console.error('Fehler beim Laden:', err);
      setError('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);

    try {
      await apiClient.post('/buchhaltung/einstellungen', {
        beraterNr,
        mandantenNr,
        kontenrahmen,
        standardErloeskonto,
        steuerberaterEmail,
        autoVersandAktiv,
        autoVersandRhythmus
      });

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      loadEinstellungen();
    } catch (err) {
      setError('Fehler beim Speichern: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleLoadVorschau = async () => {
    if (!exportVon || !exportBis) {
      setError('Bitte Zeitraum angeben');
      return;
    }

    setLoadingVorschau(true);
    setError('');
    setVorschau(null);

    try {
      const response = await apiClient.get('/buchhaltung/export/vorschau', {
        params: { von: exportVon, bis: exportBis }
      });
      setVorschau(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setVorschau({ anzahl: 0, rechnungen: [], summe: { netto: 0, ust: 0, brutto: 0 } });
      } else {
        setError(err.response?.data?.error || 'Fehler beim Laden der Vorschau');
      }
    } finally {
      setLoadingVorschau(false);
    }
  };

  const handleExport = async () => {
    if (!exportVon || !exportBis) {
      setError('Bitte Zeitraum angeben');
      return;
    }

    if (!beraterNr || !mandantenNr) {
      setError('Bitte zuerst Berater-Nr. und Mandanten-Nr. speichern');
      return;
    }

    setExporting(true);
    setError('');

    try {
      const response = await apiClient.get('/buchhaltung/export/datev', {
        params: { von: exportVon, bis: exportBis },
        responseType: 'blob'
      });

      // Download ausloesen
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Dateiname aus Header oder generieren
      const contentDisposition = response.headers['content-disposition'];
      let filename = `EXTF_buchungen_${new Date(exportVon).getFullYear()}.csv`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.data) {
        // Blob zu Text konvertieren fuer Fehlermeldung
        const text = await err.response.data.text();
        try {
          const json = JSON.parse(text);
          setError(json.error || 'Fehler beim Export');
        } catch {
          setError('Fehler beim Export');
        }
      } else {
        setError(err.message || 'Fehler beim Export');
      }
    } finally {
      setExporting(false);
    }
  };

  const handleSendNow = async () => {
    if (!exportVon || !exportBis) {
      setError('Bitte Zeitraum angeben');
      return;
    }
    if (!steuerberaterEmail) {
      setError('Bitte zuerst die E-Mail-Adresse des Steuerberaters speichern');
      return;
    }
    if (!confirm(`DATEV-Export für ${new Date(exportVon).toLocaleDateString('de-DE')} bis ${new Date(exportBis).toLocaleDateString('de-DE')} jetzt an ${steuerberaterEmail} senden?`)) return;

    setSending(true);
    setError('');
    setSendResult('');
    try {
      const response = await apiClient.post('/buchhaltung/export/senden', { von: exportVon, bis: exportBis });
      setSendResult(response.data.message || 'Export gesendet');
      loadEinstellungen();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Versand');
    } finally {
      setSending(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-card shadow-card overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Calculator className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Buchhaltung & DATEV-Export</h2>
            <p className="text-sm text-text-muted">
              Exportieren Sie Rechnungsdaten fuer Ihren Steuerberater
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
            <button onClick={() => setError('')} className="ml-auto">x</button>
          </div>
        )}

        {/* DATEV-Konfiguration */}
        <div className="p-4 bg-gray-50 rounded-lg space-y-4">
          <h3 className="font-medium flex items-center gap-2">
            <Info className="w-4 h-4" /> DATEV-Konfiguration
          </h3>
          <p className="text-sm text-text-muted">
            Diese Angaben erhalten Sie von Ihrem Steuerberater. Sie werden fuer den DATEV-Import benoetigt.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Berater-Nr.
              </label>
              <input
                type="text"
                value={beraterNr}
                onChange={(e) => setBeraterNr(e.target.value)}
                placeholder="z.B. 12345"
                disabled={readOnly}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm disabled:bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mandanten-Nr.
              </label>
              <input
                type="text"
                value={mandantenNr}
                onChange={(e) => setMandantenNr(e.target.value)}
                placeholder="z.B. 10001"
                disabled={readOnly}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm disabled:bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kontenrahmen
              </label>
              <select
                value={kontenrahmen}
                onChange={(e) => setKontenrahmen(e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm disabled:bg-gray-100"
              >
                {KONTENRAHMEN_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Standard-Erloeskonto
              </label>
              <input
                type="text"
                value={standardErloeskonto}
                onChange={(e) => setStandardErloeskonto(e.target.value)}
                placeholder="8400"
                disabled={readOnly}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm disabled:bg-gray-100"
              />
              <p className="text-xs text-text-muted mt-1">
                SKR03: 8400 (Erloese 19% USt) | SKR04: 4400
              </p>
            </div>
          </div>

          {/* Speichern Button */}
          {!readOnly && (
            <div className="pt-2">
              {saved && (
                <div className="flex items-center gap-2 p-3 mb-4 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                  <Check className="w-4 h-4" />
                  Einstellungen gespeichert
                </div>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Speichern...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Einstellungen speichern
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Automatischer Versand an den Steuerberater */}
        <div className="p-4 bg-gray-50 rounded-lg space-y-4">
          <h3 className="font-medium flex items-center gap-2">
            <Mail className="w-4 h-4" /> Automatischer Versand an den Steuerberater
          </h3>
          <p className="text-sm text-text-muted">
            WerkPilot sendet den DATEV-Export nach Abschluss jedes Zeitraums automatisch per E-Mail an Ihren Steuerberater
            (Absender: WerkPilot-Systemadresse, Antworten gehen an Ihre Firmen-E-Mail). Sie erhalten jeweils eine Benachrichtigung.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">E-Mail des Steuerberaters</label>
              <input
                type="email"
                value={steuerberaterEmail}
                onChange={(e) => setSteuerberaterEmail(e.target.value)}
                placeholder="kanzlei@steuerberater.de"
                disabled={readOnly}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rhythmus</label>
              <select
                value={autoVersandRhythmus}
                onChange={(e) => setAutoVersandRhythmus(e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm disabled:bg-gray-100"
              >
                <option value="monatlich">Monatlich (jeweils der Vormonat)</option>
                <option value="quartal">Quartalsweise (jeweils das Vorquartal)</option>
              </select>
            </div>
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={autoVersandAktiv}
              onChange={(e) => setAutoVersandAktiv(e.target.checked)}
              disabled={readOnly}
              className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm font-medium text-gray-700">Automatischen Versand aktivieren</span>
          </label>

          <div className="flex flex-wrap items-center gap-4 text-xs text-text-muted">
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              Letzter Versand: {autoVersandInfo.letzterVersand ? new Date(autoVersandInfo.letzterVersand).toLocaleString('de-DE') : 'noch keiner'}
            </span>
            {autoVersandAktiv && autoVersandInfo.naechsterZeitraum && (
              <span>
                Nächster automatischer Versand: {autoVersandInfo.letzterZeitraum === autoVersandInfo.naechsterZeitraum.key
                  ? `nach Abschluss des laufenden ${autoVersandRhythmus === 'quartal' ? 'Quartals' : 'Monats'}`
                  : `${autoVersandInfo.naechsterZeitraum.label} (am nächsten Morgen um 07:00 Uhr)`}
              </span>
            )}
          </div>

          <p className="text-xs text-text-muted">
            Hinweis: Die Einstellungen werden über „Einstellungen speichern" oben übernommen. Für den automatischen Versand müssen Berater-Nr. und Mandanten-Nr. hinterlegt sein.
          </p>
        </div>

        {/* DATEV-Export */}
        <div className="p-4 bg-gray-50 rounded-lg space-y-4">
          <h3 className="font-medium flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4" /> DATEV-Export
          </h3>
          <p className="text-sm text-text-muted">
            Exportiert alle Rechnungen im ausgewaehlten Zeitraum als DATEV-kompatible CSV-Datei (EXTF-Format).
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Von
              </label>
              <input
                type="date"
                value={exportVon}
                onChange={(e) => {
                  setExportVon(e.target.value);
                  setVorschau(null);
                }}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Bis
              </label>
              <input
                type="date"
                value={exportBis}
                onChange={(e) => {
                  setExportBis(e.target.value);
                  setVorschau(null);
                }}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              />
            </div>
          </div>

          {/* Vorschau Button */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleLoadVorschau}
              disabled={loadingVorschau || !exportVon || !exportBis}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-button hover:bg-gray-50 transition disabled:opacity-50 text-sm"
            >
              {loadingVorschau ? (
                <>
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  Laden...
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  Vorschau anzeigen
                </>
              )}
            </button>

            <button
              onClick={handleExport}
              disabled={exporting || !exportVon || !exportBis || !beraterNr || !mandantenNr}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-button font-medium hover:bg-blue-700 transition disabled:opacity-50 text-sm"
            >
              {exporting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Exportieren...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  DATEV-Export herunterladen
                </>
              )}
            </button>

            {!readOnly && (
              <button
                onClick={handleSendNow}
                disabled={sending || !exportVon || !exportBis || !beraterNr || !mandantenNr || !steuerberaterEmail}
                title={!steuerberaterEmail ? 'Bitte zuerst die Steuerberater-E-Mail speichern' : ''}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50 text-sm"
              >
                {sending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sende...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Jetzt an Steuerberater senden
                  </>
                )}
              </button>
            )}
          </div>

          {sendResult && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
              <Check className="w-4 h-4" />
              {sendResult}
            </div>
          )}

          {/* Vorschau Ergebnis */}
          {vorschau && (
            <div className="mt-4 p-4 bg-white border border-gray-200 rounded-lg">
              <h4 className="font-medium mb-3">
                {vorschau.anzahl} Rechnung{vorschau.anzahl !== 1 ? 'en' : ''} im Zeitraum
              </h4>

              {vorschau.anzahl > 0 ? (
                <>
                  {/* Summen */}
                  <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
                    <div>
                      <span className="text-xs text-text-muted">Netto</span>
                      <div className="font-semibold">{formatCurrency(vorschau.summe.netto)}</div>
                    </div>
                    <div>
                      <span className="text-xs text-text-muted">USt</span>
                      <div className="font-semibold">{formatCurrency(vorschau.summe.ust)}</div>
                    </div>
                    <div>
                      <span className="text-xs text-text-muted">Brutto</span>
                      <div className="font-semibold text-primary">{formatCurrency(vorschau.summe.brutto)}</div>
                    </div>
                  </div>

                  {/* Rechnungsliste */}
                  <div className="max-h-48 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="text-left text-text-muted border-b">
                        <tr>
                          <th className="py-2">Nr.</th>
                          <th className="py-2">Datum</th>
                          <th className="py-2">Kunde</th>
                          <th className="py-2 text-right">Betrag</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vorschau.rechnungen.map((r, i) => (
                          <tr key={i} className="border-b border-gray-100">
                            <td className="py-2 font-mono text-xs">{r.rechnungsnummer}</td>
                            <td className="py-2">{new Date(r.datum).toLocaleDateString('de-DE')}</td>
                            <td className="py-2 truncate max-w-[150px]">{r.kunde}</td>
                            <td className="py-2 text-right">{formatCurrency(r.brutto)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <p className="text-text-muted text-sm">
                  Keine exportierbaren Rechnungen gefunden. Es werden nur versendete, bezahlte, ueberfaellige oder gemahnte Rechnungen exportiert.
                </p>
              )}
            </div>
          )}

          {/* Hinweis */}
          {(!beraterNr || !mandantenNr) && (
            <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg text-sm">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                Bitte hinterlegen Sie zuerst die Berater-Nr. und Mandanten-Nr. fuer den DATEV-Export.
                Diese Angaben erhalten Sie von Ihrem Steuerberater.
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BuchhaltungSettings;
